"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { NITRO_ENABLED, NITRO_MOBILE_PLACEMENT_ID } from "./config";

declare global {
  interface Window {
    nitroAds?: {
      createAd: (id: string, options: Record<string, unknown>) => unknown;
      queue?: unknown[];
    };
  }
}

/** Creates exactly one 320-767px placement per mounted base page.
 * On client navigation we remount the DOM div, as Nitro recommends for SPAs.
 * Only enable after Nitro has confirmed demand eligibility and CMP readiness.
 */
export default function NitroMobileBaseAd() {
  const pathname = usePathname();
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 320px) and (max-width: 767px)");
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  if (!NITRO_ENABLED || !mobile) return null;
  return <NitroPlacement key={pathname} />;
}

function NitroPlacement() {
  useEffect(() => {
    // Loaded via the official Nitro head bootstrap; queue exists before loader finishes.
    if (!window.nitroAds?.createAd) {
      console.warn("[Nitro] bootstrap not available");
      return;
    }
    try {
      Promise.resolve(window.nitroAds.createAd(NITRO_MOBILE_PLACEMENT_ID, {
        height: 250,
        delayLoading: true,
        report: {
          enabled: true,
          icon: true,
          wording: "Report Ad",
          position: "top-right",
        },
        mediaQuery: "(min-width: 320px) and (max-width: 767px)",
      })).catch((err: unknown) => console.error("[Nitro] createAd failed", err));
    } catch (err) {
      console.error("[Nitro] placement initialization failed", err);
    }
    // Do not manually remove Nitropay iframes: unmount removes the container.
  }, []);

  return (
    <div aria-label="Advertisement" data-bfc-nitro="base-inline-mobile" style={{ width: "100%", minHeight: 250, margin: "24px auto", textAlign: "center" }}>
      <div id={NITRO_MOBILE_PLACEMENT_ID} style={{ height: 250, maxWidth: "100%" }} />
    </div>
  );
}
